/* eslint-disable @typescript-eslint/consistent-type-assertions */
/* istanbul ignore file */

import { logger as LOG, logger } from '@garnish/logger';
import { assign, createMachine, type InterpreterFrom, raise } from 'xstate';

import { type Predictions } from '@sg/garnish';

import {
  getActiveLocation,
  getActiveRecentDeliveryLocation,
  getActiveRecentLocation,
  getSearchAreaBasedOnCurrentPosition,
  setLocationWarningStorageRecord,
} from './actions';
import {
  type LocationGroups,
  type LocationSearchMachineContext,
  type LocationSearchMachineEvents,
  type LocationSearchMachineServices,
} from './LocationSearch.types';
import {
  LOCATION_SEARCH_MACHINE_DEBOUNCE_DELAY_MS,
  LOCATION_SEARCH_MACHINE_MINIMAL_CHARACTERS_FOR_SEARCH,
} from './LocationSearchMachine.constants';
import {
  addNewDeliveryAddress,
  addNewDeliveryAddressV2,
  checkIfShouldShowLocationWarning,
  fetchPredictions,
  getDefaultDeliveryLocation,
  getDeliveryAddressPredictions,
  getDeliveryLocationByPlaceId,
  getDeliveryLocationDetailsByAddress,
  getNearbyLocations,
  getRadarDeliveryAddressPredictions,
  getRecentAndNearbyLocations,
  searchByGeo,
  searchLocationsByRegion,
  searchLocationsByRestaurantName,
  selectPrediction,
} from './services';
import { expandBoundsToMinimumRadius } from './utils';

import type { Client } from 'urql';

// ─────────────────────────────────────────────────────────────────────────────

export const createLocationSearchMachine = (params: CreateLocationSearchMachineParams) => {
  const {
    client,
    lastInteractedStoreAddressId,
    googlePlacesApiKey,
    shouldUseCurrentPosition = true,
    minimalCharactersForSearch = LOCATION_SEARCH_MACHINE_MINIMAL_CHARACTERS_FOR_SEARCH,
    shouldUseRadarForPickup,
    shouldUseRadarForDelivery,
    shouldUseDeliveryDisclosureFields,
    shouldUseOutpostDisclosureFields,
  } = params;

  return createMachine(
    {
      // eslint-disable-next-line @typescript-eslint/consistent-type-imports
      tsTypes: {} as import('./LocationSearchMachine.typegen').Typegen0,
      predictableActionArguments: true,
      preserveActionOrder: true,
      schema: {
        context: {} as LocationSearchMachineContext,
        events: {} as LocationSearchMachineEvents,
        services: {} as LocationSearchMachineServices,
      },
      context: {
        //
        // ─── Search Type ─────────────────────────────

        locationSearchType: undefined,

        // ─── Locations ───────────────────────────────

        searchString: '',
        searchArea: undefined,
        selectedSearchArea: undefined,
        currentPosition: undefined,
        locations: undefined,
        activeLocation: undefined,

        // ─── Predictions ─────────────────────────────

        baseLocation: undefined,
        baseLocationRadiusConfig: {
          SMALLEST: 5,
          SMALL: 15,
          MEDIUM: 40,
          LARGE: 200,
        },
        predictionsSearchQuery: undefined,
        predictionLocations: undefined,
        predictions: [],

        predictionTabs: [
          'location-prediction-tab.predictions',
          'location-prediction-tab.locations',
        ],
        activePredictionTab: 'location-prediction-tab.predictions',

        // ─── Recent And Nearby Locations ─────────────

        recentLocations: undefined,
        nearbyLocations: undefined,

        // ─── Delivery Location ───────────────────────

        deliverySearchString: '',
        deliveryLocation: undefined,
        deliveryAddressPredictions: [],
        selectedDeliveryAddressDetails: undefined,
        selectedDeliveryAddressPredictionDetails: undefined,
        selectedDeliveryAddressPredictionId: undefined,
        selectedDeliveryAddressPredictionPlaceAddress: undefined,

        // ─────────────────────────────────────────────

        focusedPinId: undefined,
        focusedLocationId: undefined,

        // ─── Flags ───────────────────────────────────

        shouldUseImprovedDeliverySearch: false,
      },
      type: 'parallel',
      states: {
        searchType: {
          initial: 'undetermined',
          states: {
            undetermined: {
              id: 'search-type-undetermined',
              always: [
                {
                  target: '#search-type-pickup',
                  cond: 'checkIfPickupOrderChannel',
                },
                {
                  target: '#search-type-delivery',
                  cond: 'checkIfDeliveryOrderChannel',
                },
                {
                  target: '#search-type-outpost',
                  cond: 'checkIfOutpostOrderChannel',
                },
                {
                  target: '#search-type-pickup',
                  cond: 'checkIfInitialOrderChannelEnabled',
                },
              ],
              on: {
                SET_LOCATION_SEARCH_TYPE_PICKUP: {
                  target: '#search-type-pickup',
                },
                SET_LOCATION_SEARCH_TYPE_DELIVERY: {
                  target: '#search-type-delivery',
                },
                SET_LOCATION_SEARCH_TYPE_OUTPOST: {
                  target: '#search-type-outpost',
                },
              },
            },
            pickup: {
              id: 'search-type-pickup',
              entry: [
                'onPickup',
                'onLocationTypeChangeEvent',
                'clearFocusedLocations',
                'setActivePredictionTabForPickup',
                raise('SEARCH_FOR_INITIAL_LOCATIONS_BY_CURRENT_POSITION'),
              ],
              on: {
                SET_LOCATION_SEARCH_TYPE_DELIVERY: {
                  target: '#search-type-delivery',
                },
                SET_LOCATION_SEARCH_TYPE_OUTPOST: {
                  target: '#search-type-outpost',
                },
                SEARCH_BY_SELECTED_PREDICTION_GEO: {
                  target: '#search-by-geo',
                },
                SEARCH_BY_AREA: {
                  target: '#search-by-area',
                },
                SEARCH_BY_CURRENT_POSITION: [
                  {
                    target: '#search-by-area',
                    actions: ['setSearchAreaToCurrentPosition', 'onUserLocationChanged'],
                    cond: 'checkIfShouldFetchLocationsByCurrentPosition',
                  },
                  { actions: ['onSearchByCurrentPositionError'] },
                ],
                SET_SEARCH_AREA: {
                  actions: ['setSearchArea'],
                },
                SET_FOCUSED_PIN_ID: {
                  actions: ['setFocusedPinId', 'onFocusedPinIdChangeEvent'],
                },
                SET_FOCUSED_CARD_ID: {
                  actions: ['setFocusedCardId'],
                },
                NAVIGATE_TO_LOCATION: {
                  actions: ['setActiveLocation', 'onNavigateToLocationEvent'],
                  target: '#check-for-pickup-location-warning',
                },
              },
            },
            delivery: {
              id: 'search-type-delivery',
              entry: [
                'onDelivery',
                'onLocationTypeChangeEvent',
                'clearFocusedLocations',
                raise('SET_DEFAULT_DELIVERY_LOCATION'),
              ],
              on: {
                SET_LOCATION_SEARCH_TYPE_PICKUP: {
                  target: '#search-type-pickup',
                },
                SET_LOCATION_SEARCH_TYPE_OUTPOST: {
                  target: '#search-type-outpost',
                },
                SEARCH_BY_DELIVERY_ADDRESS: {
                  target: '#get-default-delivery-location',
                },
                SET_DELIVERY_SEARCH_STRING: [
                  {
                    target: '#search-delivery-addresses',
                    actions: [
                      'setDeliverySearchString',
                      'clearDeliveryLocation',
                      'clearFocusedLocations',
                    ],
                    cond: 'checkIfEnteredMinChars',
                  },
                  {
                    actions: [
                      'setDeliverySearchString',
                      'clearDeliveryLocation',
                      'clearFocusedLocations',
                    ],
                  },
                ],
                SET_DELIVERY_PREDICTION_PLACE_ID: {
                  target: '#search-by-delivery-place-id',
                  actions: [
                    'clearSelectedDeliveryAddress',
                    'setSelectedDeliveryAddressPredictionPlaceId',
                  ],
                },
                SET_DELIVERY_PREDICTION_DETAILS: {
                  target: '#search-by-delivery-location-address',
                  actions: ['clearSelectedDeliveryAddress', 'setSelectedDeliveryAddressPrediction'],
                },
                SET_DELIVERY_CUSTOMER_ADDRESS: {
                  target: '#search-by-delivery-place-id',
                  actions: ['clearSelectedDeliveryAddress', 'setSelectedDeliveryAddressDetails'],
                },
                NAVIGATE_TO_DELIVERY_LOCATION: [
                  {
                    target: '#showing-delivery-address-form',
                    cond: 'checkIfShouldShowDeliveryAddressForm',
                  },
                  {
                    target: '#add-new-delivery-address',
                    cond: 'checkIfShouldAddNewDeliveryLocation',
                  },
                  {
                    target: '#navigating-to-delivery',
                    actions: 'onNavigateToDeliveryLocationEvent',
                  },
                ],
              },
            },
            outpost: {
              id: 'search-type-outpost',
              entry: [
                'onOutpost',
                'onLocationTypeChangeEvent',
                'clearFocusedLocations',
                'setActivePredictionTabForOutpost',
                raise('SEARCH_FOR_INITIAL_LOCATIONS_BY_CURRENT_POSITION'),
              ],
              on: {
                SET_LOCATION_SEARCH_TYPE_PICKUP: {
                  target: '#search-type-pickup',
                },
                SET_LOCATION_SEARCH_TYPE_DELIVERY: {
                  target: '#search-type-delivery',
                },
                SEARCH_BY_SELECTED_PREDICTION_GEO: {
                  target: '#search-by-geo',
                },
                SEARCH_BY_AREA: {
                  target: '#search-by-area',
                },
                SEARCH_BY_CURRENT_POSITION: {
                  target: '#search-by-area',
                  actions: ['setSearchAreaToCurrentPosition', 'onUserLocationChanged'],
                  cond: 'checkIfShouldFetchLocationsByCurrentPosition',
                },
                SET_SEARCH_AREA: {
                  actions: ['setSearchArea'],
                },
                SET_FOCUSED_PIN_ID: {
                  actions: ['setFocusedPinId'],
                },
                SET_FOCUSED_CARD_ID: {
                  actions: ['setFocusedCardId'],
                },
                NAVIGATE_TO_LOCATION: {
                  actions: ['setActiveLocation', 'onNavigateToLocationEvent'],
                  target: '#check-for-outpost-location-warning',
                },
              },
            },
          },
        },
        search: {
          initial: 'setup',
          states: {
            setup: {
              initial: 'waiting',
              states: {
                waiting: {
                  always: [
                    {
                      target: '#setup-recent-and-nearby-locations',
                      cond: 'checkIfInitialOrderChannelEnabledAndSignedIn',
                    },
                    {
                      target: '#setup-nearby-locations',
                      cond: 'checkIfInitialOrderChannelEnabled',
                    },
                    {
                      target: ['#search-type-pickup', '#setup-recent-and-nearby-locations'],
                      cond: 'checkIfSignedIn',
                    },
                    {
                      target: ['#search-type-pickup', '#setup-nearby-locations'],
                    },
                  ],
                },
                setupRecentAndNearbyLocations: {
                  id: 'setup-recent-and-nearby-locations',
                  invoke: {
                    src: 'getRecentAndNearbyLocations',
                    onDone: {
                      target: '#finish-setup',
                      actions: ['onRecentAndNearbyLocationsDone', 'onCurrentPositionChanged'],
                    },
                    onError: {
                      target: '#idle',
                    },
                  },
                  after: {
                    10_000: '#idle',
                  },
                  on: {
                    // NOTE: Skip setup when customer searches for predictions during setup.
                    SEARCH_FOR_PREDICTIONS: {
                      target: '#idle',
                    },
                  },
                },
                setupNearbyLocations: {
                  id: 'setup-nearby-locations',
                  invoke: {
                    src: 'getNearbyLocations',
                    onDone: {
                      target: '#finish-setup',
                      actions: ['onNearbyLocationsDone', 'onCurrentPositionChanged'],
                    },
                    onError: {
                      target: '#idle',
                    },
                  },
                  after: {
                    100_000: '#idle',
                  },
                  on: {
                    // NOTE: Skip setup when customer searches for predictions during setup.
                    SEARCH_FOR_PREDICTIONS: {
                      target: '#idle',
                    },
                  },
                },
                finishSetup: {
                  id: 'finish-setup',
                  always: [
                    {
                      target: '#recent-and-nearby-locations',
                      cond: 'checkIfHaveRecentOrNearbyLocations',
                    },
                    {
                      target: '#idle',
                    },
                  ],
                  type: 'final',
                },
              },
            },
            idle: {
              id: 'idle',
              on: {
                SET_DEFAULT_DELIVERY_LOCATION: {
                  target: '#get-default-delivery-location',
                  cond: 'checkIfShouldGetDefaultDeliveryLocationOnEntry',
                },
                SEARCH_FOR_INITIAL_LOCATIONS_BY_CURRENT_POSITION: {
                  target: '#search-by-area',
                  cond: 'checkIfShouldFetchInitialLocationsByCurrentPosition',
                },
                SHOW_RECENT_AND_NEARBY_LOCATIONS: {
                  target: '#recent-and-nearby-locations',
                  cond: 'checkIfHaveRecentOrNearbyLocations',
                },
              },
            },
            showingDeliveryAddressForm: {
              id: 'showing-delivery-address-form',
              on: {
                CANCEL_DELIVERY_ADDRESS_FORM: 'idle',
                SUBMIT_DELIVERY_ADDRESS_FORM: {
                  actions: ['onDeliveryFormFilled'],
                  target: '#add-new-delivery-address',
                },
              },
            },
            recentAndNearbyLocations: {
              id: 'recent-and-nearby-locations',
              initial: 'idle',
              entry: ['clearBaseLocation'],
              states: {
                idle: {
                  id: 'recent-and-nearby-locations.idle',
                  on: {
                    NAVIGATE_TO_RECENT_PICKUP_LOCATION: {
                      actions: ['setActiveRecentLocation', 'onNavigateToLocationEvent'],
                      target: '#recent-and-nearby-locations.navigating-to-pickup',
                    },
                    NAVIGATE_TO_RECENT_OUTPOST_LOCATION: {
                      actions: ['setActiveRecentLocation', 'onNavigateToLocationEvent'],
                      target: '#recent-and-nearby-locations.navigating-to-outpost',
                    },
                    NAVIGATE_TO_RECENT_DELIVERY_LOCATION: [
                      {
                        actions: [
                          'setActiveRecentDeliveryLocation',
                          'onNavigateToDeliveryLocationEvent',
                        ],
                        target: '#recent-and-nearby-locations.navigating-to-delivery',
                      },
                    ],
                  },
                },
                warning: {
                  on: {
                    LOCATION_WARNING_CANCEL: {
                      target: '#recent-and-nearby-locations.idle',
                      actions: ['clearActiveLocation'],
                    },
                  },
                  initial: 'waiting',
                  states: {
                    waiting: {
                      description: 'A placeholder state',
                    },
                    pickupLocation: {
                      id: 'recent-and-nearby-locations.check-pickup-location-warning',
                      on: {
                        LOCATION_WARNING_CONFIRM: {
                          target: '#recent-and-nearby-locations.navigating-to-pickup',
                          actions: [
                            'setLocationWarningStorageRecord',
                            'onLocationWarningConfirmEvent',
                          ],
                        },
                      },
                    },
                    outpostLocation: {
                      id: 'recent-and-nearby-locations.check-outpost-location-warning',
                      on: {
                        LOCATION_WARNING_CONFIRM: {
                          target: '#recent-and-nearby-locations.navigating-to-outpost',
                          actions: [
                            'setLocationWarningStorageRecord',
                            'onLocationWarningConfirmEvent',
                          ],
                        },
                      },
                    },
                  },
                },
                navigating: {
                  initial: 'waiting',
                  states: {
                    waiting: {
                      description: 'A placeholder state',
                    },
                    toPickup: {
                      id: 'recent-and-nearby-locations.navigating-to-pickup',
                      entry: ['navigateToLocation'],
                      always: { target: '#recent-and-nearby-locations.idle' },
                    },
                    toOutpost: {
                      id: 'recent-and-nearby-locations.navigating-to-outpost',
                      entry: ['navigateToLocation'],
                      always: { target: '#recent-and-nearby-locations.idle' },
                    },
                    toDelivery: {
                      id: 'recent-and-nearby-locations.navigating-to-delivery',
                      entry: ['navigateToDeliveryLocation'],
                      always: { target: '#recent-and-nearby-locations.idle' },
                    },
                  },
                },
              },
            },
            loading: {
              states: {
                //
                // ─── Locations ───────────────────────────────

                searchByArea: {
                  id: 'search-by-area',
                  entry: ['clearSearchString', 'setSelectedSearchArea'],
                  invoke: {
                    src: 'searchByArea',
                    onDone: {
                      target: '#idle',
                      actions: [
                        'onSearchByAreaDone',
                        'onSearchByAreaEvent',
                        'clearFocusedLocations',
                      ],
                    },
                    onError: {
                      target: '#idle',
                      actions: ['onSearchByAreaError'],
                    },
                  },
                },
                searchByGeo: {
                  id: 'search-by-geo',
                  invoke: {
                    src: 'searchByGeo',
                    onDone: {
                      target: '#idle',
                      actions: ['onSearchByGeoDone', 'clearFocusedLocations'],
                    },
                    onError: {
                      target: '#idle',
                    },
                  },
                },

                // ─── Locations Warnings ──────────────────────

                checkForPickupLocationWarning: {
                  id: 'check-for-pickup-location-warning',
                  invoke: {
                    src: 'checkIfShouldShowPickupLocationWarning',
                    onDone: [
                      {
                        target: '#warning-pickup-location',
                        cond: 'checkIfShouldShowLocationWarning',
                      },
                      {
                        target: '#navigating-to-pickup',
                      },
                    ],
                    onError: {
                      target: '#idle',
                      actions: ['onCheckForLocationWarningError'],
                    },
                  },
                },
                checkForOutpostLocationWarning: {
                  id: 'check-for-outpost-location-warning',
                  invoke: {
                    src: 'checkIfShouldShowOutpostLocationWarning',
                    onDone: [
                      {
                        target: '#warning-outpost-location',
                        cond: 'checkIfShouldShowLocationWarning',
                      },
                      {
                        target: '#navigating-to-outpost',
                      },
                    ],
                    onError: {
                      target: '#idle',
                      actions: ['onCheckForLocationWarningError'],
                    },
                  },
                },

                // ─── Delivery Location ───────────────────────

                searchByDeliveryLocationPlaceId: {
                  id: 'search-by-delivery-place-id',
                  invoke: {
                    src: 'searchByDeliveryLocationPlaceId',
                    onDone: {
                      target: '#idle',
                      actions: [
                        'clearFocusedLocations',
                        'onSearchByDeliveryPlaceIdDone',
                        'onSearchByDeliveryPlaceIdEvent',
                      ],
                    },
                    onError: {
                      target: '#idle',
                      actions: ['onSearchByDeliveryPlaceIdError'],
                    },
                  },
                },
                searchByDeliveryLocationAddress: {
                  id: 'search-by-delivery-location-address',
                  invoke: {
                    src: 'searchByDeliveryLocationAddress',
                    onDone: {
                      target: '#idle',
                      actions: [
                        'clearFocusedLocations',
                        'onSearchByDeliveryPlaceIdDone',
                        'onSearchByDeliveryPlaceIdEvent',
                      ],
                    },
                    onError: {
                      target: '#idle',
                      actions: ['onSearchByDeliveryPlaceIdError'],
                    },
                  },
                },
                getDefaultDeliveryLocation: {
                  id: 'get-default-delivery-location',
                  invoke: {
                    src: 'getDefaultDeliveryLocation',
                    onDone: {
                      target: '#idle',
                      actions: [
                        'clearFocusedLocations',
                        'onSearchByDeliveryPlaceIdDone',
                        'onSearchByDeliveryPlaceIdEvent',
                      ],
                    },
                    onError: {
                      target: '#idle',
                      actions: ['onSearchByDeliveryPlaceIdError'],
                    },
                  },
                },
                searchDeliveryAddresses: {
                  id: 'search-delivery-addresses',
                  invoke: {
                    src: 'searchDeliveryAddresses',
                    onDone: {
                      target: '#idle',
                      actions: ['onSearchDeliveryAddressesDone'],
                    },
                    onError: {
                      target: '#idle',
                      actions: ['onSearchDeliveryAddressesError'],
                    },
                  },
                },
                addNewDeliveryAddress: {
                  id: 'add-new-delivery-address',
                  invoke: {
                    src: 'addNewDeliveryAddress',
                    onDone: {
                      target: '#navigating-to-delivery',
                      actions: ['onAddDeliveryAddressIdDone'],
                    },
                    onError: {
                      target: '#idle',
                      actions: ['onAddDeliveryAddressIdError'],
                    },
                  },
                },
              },
            },
            warning: {
              initial: 'waiting',
              states: {
                waiting: {
                  description: 'A placeholder state',
                },
                pickupLocation: {
                  id: 'warning-pickup-location',
                  on: {
                    LOCATION_WARNING_CANCEL: {
                      target: '#idle',
                      actions: ['clearActiveLocation'],
                    },
                    LOCATION_WARNING_CONFIRM: {
                      target: '#navigating-to-pickup',
                      actions: ['setLocationWarningStorageRecord', 'onLocationWarningConfirmEvent'],
                    },
                  },
                },
                outpostLocation: {
                  id: 'warning-outpost-location',
                  on: {
                    LOCATION_WARNING_CANCEL: {
                      target: '#idle',
                      actions: ['clearActiveLocation'],
                    },
                    LOCATION_WARNING_CONFIRM: {
                      target: '#navigating-to-outpost',
                      actions: ['setLocationWarningStorageRecord', 'onLocationWarningConfirmEvent'],
                    },
                  },
                },
              },
            },
            navigating: {
              initial: 'waiting',
              states: {
                waiting: {
                  description: 'A placeholder state',
                },
                toPickup: {
                  id: 'navigating-to-pickup',
                  entry: ['navigateToLocation'],
                  always: { target: '#idle' },
                },
                toOutpost: {
                  id: 'navigating-to-outpost',
                  entry: ['navigateToLocation'],
                  always: { target: '#idle' },
                },
                toDelivery: {
                  id: 'navigating-to-delivery',
                  entry: ['navigateToDeliveryLocation'],
                  always: { target: '#idle' },
                },
              },
            },
          },
        },
        searchForPredictions: {
          description: 'A state to control the predictions search flow',
          id: 'search-for-predictions',
          on: {
            SEARCH_BY_AREA: '.resetting',
            SEARCH_BY_CURRENT_POSITION: '.resetting',
            SELECT_PREDICTION: '.selecting',

            SET_PREDICTION_TAB_TO_PREDICTIONS: {
              actions: ['setPredictionTabToPredictions'],
            },
            SET_PREDICTION_TAB_TO_LOCATIONS: {
              actions: ['setPredictionTabToLocations'],
            },
          },
          initial: 'idle',
          states: {
            idle: {
              on: {
                SEARCH_FOR_PREDICTIONS: {
                  target: 'determining',
                  actions: ['setPredictionsSearchQuery'],
                },
              },
            },
            determining: {
              always: [
                {
                  target: 'debouncing',
                  cond: 'checkIfAcceptableSearchQuery',
                },
                {
                  target: 'idle',
                  actions: ['clearSearchPredictions'],
                },
              ],
            },
            debouncing: {
              after: {
                [LOCATION_SEARCH_MACHINE_DEBOUNCE_DELAY_MS]: 'searching',
              },
              on: {
                SEARCH_FOR_PREDICTIONS: {
                  target: 'determining',
                  actions: ['setPredictionsSearchQuery'],
                },
              },
            },
            searching: {
              on: {
                SEARCH_FOR_PREDICTIONS: {
                  target: 'determining',
                  actions: ['setPredictionsSearchQuery'],
                },
              },
              entry: ['onSearchForPredictions'],
              invoke: {
                src: 'getPredictionsAndRestaurants',
                onDone: {
                  target: 'idle',
                  actions: ['onSearchForPredictionsAndRestaurantsDone'],
                },
                onError: {
                  target: 'resetting',
                  actions: ['onSearchPredictionsAndRestaurantsError'],
                },
              },
            },
            selecting: {
              entry: ['clearSelectedSearchArea'],
              invoke: {
                src: 'selectPrediction',
                onDone: {
                  target: 'idle',
                  actions: [
                    'clearSearchPredictions',
                    'onSelectPredictionDone',
                    'raiseSearchBySelectedPredictionGeo',
                  ],
                },
                onError: {
                  target: 'resetting',
                },
              },
            },
            resetting: {
              always: { target: 'idle' },
              entry: ['clearSearchPredictions', 'clearBaseLocation'],
            },
          },
        },
      },
    },
    {
      services: {
        //
        // ─── Locations ───────────────────────────────

        getRecentAndNearbyLocations: async () =>
          getRecentAndNearbyLocations({
            client,
            shouldUseOutpostDisclosureFields,
            shouldUseDeliveryDisclosureFields,
          }),
        getNearbyLocations: async () =>
          getNearbyLocations({
            client,
            shouldUseOutpostDisclosureFields,
            shouldUseDeliveryDisclosureFields,
          }),

        searchByArea: async (context) => {
          return searchLocationsByRegion({ client, context });
        },

        // ─── Predictions ─────────────────────────────

        getPredictionsAndRestaurants: async (context) => {
          const { predictionsSearchQuery, currentPosition } = context;

          if (!predictionsSearchQuery)
            return {
              predictions: [] as Predictions,
              locations: { pickup: [], outpost: [] } as LocationGroups,
            };

          const near = currentPosition
            ? `${currentPosition.latitude},${currentPosition.longitude}`
            : undefined;

          const [predictionsResponse, locations] = await Promise.all([
            fetchPredictions(predictionsSearchQuery, {
              shouldUseRadar: shouldUseRadarForPickup,
              near,
            }),
            searchLocationsByRestaurantName({ client, context }),
          ]);

          return {
            predictions: predictionsResponse?.predictions,
            locations,
          };
        },
        selectPrediction: async (context, event) =>
          selectPrediction({ predictions: context.predictions, event }),

        searchByGeo: async (context) => searchByGeo({ client, context }),

        // ─── Location Warning ────────────────────────

        checkIfShouldShowPickupLocationWarning: async (context) =>
          checkIfShouldShowLocationWarning(context.activeLocation),

        checkIfShouldShowOutpostLocationWarning: async (context) =>
          checkIfShouldShowLocationWarning(context.activeLocation),

        // ─── Delivery Location ───────────────────────

        searchByDeliveryLocationPlaceId: async (context) => {
          const shouldUseAddressFallback = context.shouldUseImprovedDeliverySearch;

          return getDeliveryLocationByPlaceId({
            client,
            addressDetails: context.selectedDeliveryAddressDetails,
            googlePlacesApiKey,
            googlePlaceId:
              context.selectedDeliveryAddressPredictionId ??
              context.selectedDeliveryAddressDetails?.googlePlaceId,
            googlePlaceAddress: shouldUseAddressFallback
              ? context.selectedDeliveryAddressPredictionPlaceAddress
              : undefined,
            shouldUseDisclosureFields: shouldUseDeliveryDisclosureFields,
          });
        },
        searchByDeliveryLocationAddress: async (context) => {
          return getDeliveryLocationDetailsByAddress({
            client,
            address:
              context.selectedDeliveryAddressDetails ??
              context.selectedDeliveryAddressPredictionDetails,
            shouldUseDisclosureFields: shouldUseDeliveryDisclosureFields,
          });
        },
        getDefaultDeliveryLocation: async (context) =>
          getDefaultDeliveryLocation({
            client,
            googlePlacesApiKey,
            lastInteractedStoreAddressId,
            shouldUseDisclosureFields: shouldUseDeliveryDisclosureFields,
            shouldUseImprovedDeliverySearch: context.shouldUseImprovedDeliverySearch, // prettier-ignore
          }),
        searchDeliveryAddresses: async (context) => {
          const { deliverySearchString, currentPosition } = context;

          const near = currentPosition
            ? `${currentPosition.latitude},${currentPosition.longitude}`
            : undefined;

          if (shouldUseRadarForDelivery) {
            return getRadarDeliveryAddressPredictions(client, deliverySearchString, near);
          }

          return getDeliveryAddressPredictions(client, deliverySearchString);
        },
        addNewDeliveryAddress: async (context) => {
          if (shouldUseRadarForDelivery) {
            return addNewDeliveryAddressV2(client, context.deliveryLocation);
          }

          return addNewDeliveryAddress(client, context.deliveryLocation);
        },
      },
      actions: {
        // ─── Locations Search Type ───────────────────

        onPickup: assign({
          locationSearchType: (_) => 'pickup' as const,
        }),
        onDelivery: assign({
          locationSearchType: (_) => 'delivery' as const,
        }),
        onOutpost: assign({
          locationSearchType: (_) => 'outpost' as const,
        }),
        // ─── Locations ───────────────────────────────

        onSearchByAreaDone: assign({
          locations: (_context, event) => event.data,
        }),
        onSearchByGeoDone: assign({
          locations: (_context, event) => event.data,
          predictionLocations: undefined,
          predictions: [],
          predictionsSearchQuery: undefined,
        }),
        setSearchArea: assign({
          searchArea: (_, { searchArea }) => searchArea,
        }),
        setSearchAreaToCurrentPosition: assign({
          searchArea: (context) => {
            if (!context.currentPosition) return;

            return getSearchAreaBasedOnCurrentPosition({
              currentPositionCoordinates: context.currentPosition,
              radiusInMiles: 5,
            });
          },
        }),
        setSelectedSearchArea: assign({
          selectedSearchArea: (context) => context.searchArea,
        }),
        clearSelectedSearchArea: assign({
          selectedSearchArea: (_) => undefined,
        }),
        clearSearchString: assign({
          searchString: (_) => '',
        }),
        setActiveLocation: assign({
          activeLocation: getActiveLocation,
        }),
        clearActiveLocation: assign({
          activeLocation: (_) => undefined,
        }),

        // ─── Predictions ─────────────────────────────

        raiseSearchBySelectedPredictionGeo: raise('SEARCH_BY_SELECTED_PREDICTION_GEO'),
        setPredictionsSearchQuery: assign({
          predictionsSearchQuery: (_, { searchString }) => searchString,
        }),
        clearSearchPredictions: assign(() => ({
          predictionsSearchQuery: undefined,
          predictions: [],
          predictionLocations: undefined,
        })),
        clearBaseLocation: assign({ baseLocation: undefined }),

        onSearchForPredictionsAndRestaurantsDone: assign((_, event) => ({
          predictions: event.data.predictions,
          predictionLocations: event.data.locations,
          activePredictionTab:
            event.data.predictions.length > 0
              ? 'location-prediction-tab.predictions'
              : 'location-prediction-tab.locations',
        })),
        onSelectPredictionDone: assign({
          searchString: (_, event) => event.data?.description ?? '',
          baseLocation: (_, event) => {
            const lat = event.data?.lat;
            const lng = event.data?.lng;

            if (!lat || !lng) return;

            const originalBounds = event.data?.bounds;

            // Expand bounds to ensure a minimum radius if bounds are provided
            const bounds = originalBounds
              ? expandBoundsToMinimumRadius({
                  bounds: originalBounds,
                  centerLat: lat,
                  centerLng: lng,
                })
              : undefined;

            return {
              latitude: lat,
              longitude: lng,
              name: event.data?.description ?? '',
              types: event.data?.types,
              bounds,
            };
          },
        }),

        // @ts-expect-error - For some reason, Typegen is not generating this action
        onSearchForPredictions: createLogger('Provide `onSearchForPredictions` action'),
        onSearchPredictionsAndRestaurantsError: createErrorLogger(
          'onSearchPredictionsAndRestaurantsError',
          'Failed to fetch predictions and/or restaurants',
        ),

        // ─── Prediction Tabs ─────────────────────────

        setActivePredictionTabForPickup: assign((context) => {
          const pickupLocations = context.predictionLocations?.pickup ?? [];
          const hasPickupLocations = pickupLocations.length > 0;

          const activeTabId = context.activePredictionTab;
          const isLocationTabId = activeTabId === 'location-prediction-tab.locations'; // prettier-ignore

          return {
            activePredictionTab:
              isLocationTabId && hasPickupLocations
                ? 'location-prediction-tab.locations'
                : 'location-prediction-tab.predictions',
          };
        }),
        setActivePredictionTabForOutpost: assign((context) => {
          const outpostLocations = context.predictionLocations?.outpost ?? [];
          const hasOutpostLocations = outpostLocations.length > 0;

          const activeTabId = context.activePredictionTab;
          const isLocationTabId = activeTabId === 'location-prediction-tab.locations'; // prettier-ignore

          return {
            activePredictionTab:
              isLocationTabId && hasOutpostLocations
                ? 'location-prediction-tab.locations'
                : 'location-prediction-tab.predictions',
          };
        }),
        setPredictionTabToPredictions: assign({
          activePredictionTab: 'location-prediction-tab.predictions',
        }),
        setPredictionTabToLocations: assign({
          activePredictionTab: 'location-prediction-tab.locations',
        }),

        // ─── Location Warning ────────────────────────

        setLocationWarningStorageRecord: (context) => {
          setLocationWarningStorageRecord(context.activeLocation);
        },

        // ─── Delivery Location ───────────────────────

        onSearchByDeliveryPlaceIdDone: assign({
          focusedLocationId: (_, event) => event.data?.id,
          focusedPinId: (_, event) => event.data?.id,
          deliveryLocation: (_, event) => event.data,
          deliverySearchString: (context, { data }) =>
            data?.address ?? context.deliverySearchString,
        }),

        onSearchDeliveryAddressesDone: assign({
          deliveryAddressPredictions: (_, event) => event.data,
        }),

        onDeliveryFormFilled: assign({
          deliveryLocation: (_, event) => event.address,
        }),

        onAddDeliveryAddressIdDone: assign({
          deliveryLocation: (context, event) =>
            context.deliveryLocation
              ? { ...context.deliveryLocation, addressId: event.data }
              : undefined,
        }),

        setActiveRecentLocation: assign({
          activeLocation: getActiveRecentLocation,
        }),
        setActiveRecentDeliveryLocation: assign({
          deliveryLocation: getActiveRecentDeliveryLocation,
        }),
        clearDeliveryLocation: assign({
          deliveryLocation: (_) => undefined,
        }),
        setDeliverySearchString: assign({
          deliverySearchString: (_, { searchString }) => searchString,
        }),

        clearSelectedDeliveryAddress: assign({
          selectedDeliveryAddressPredictionDetails: undefined,
          selectedDeliveryAddressPredictionPlaceAddress: undefined,
          selectedDeliveryAddressPredictionId: undefined,
          selectedDeliveryAddressDetails: undefined,
        }),
        setSelectedDeliveryAddressPredictionPlaceId: assign({
          selectedDeliveryAddressPredictionPlaceAddress: (_, { placeAddress }) => placeAddress, // prettier-ignore
          selectedDeliveryAddressPredictionId: (_, { placeId }) => placeId,
        }),
        setSelectedDeliveryAddressPrediction: assign({
          selectedDeliveryAddressPredictionDetails: (_, { predictedAddress }) => predictedAddress,
        }),
        setSelectedDeliveryAddressDetails: assign({
          selectedDeliveryAddressDetails: (_, { addressDetails }) => addressDetails,
        }),

        // ─── Recent Locations ────────────────────────

        onRecentAndNearbyLocationsDone: assign({
          currentPosition: (_context, event) => event.data.currentPositionCoordinates,
          recentLocations: (_context, event) => event.data.recentLocations,
          nearbyLocations: (_context, event) => event.data.nearbyLocations,
        }),
        onNearbyLocationsDone: assign({
          currentPosition: (_context, event) => event.data.currentPositionCoordinates,
          nearbyLocations: (_context, event) => event.data.nearbyLocations,
        }),

        // ─── Focused Pin & Card ──────────────────────

        setFocusedPinId: assign({
          focusedPinId: (_, event) => event.id,
        }),
        setFocusedCardId: assign({
          focusedLocationId: (_, event) => event.id,
        }),
        clearFocusedLocations: assign({
          focusedPinId: (_) => undefined,
          focusedLocationId: (_) => undefined,
        }),

        // ─── Navigation ──────────────────────────────

        navigateToLocation: createLogger(
          'Provide `navigateToLocation` action to handle location navigation event',
        ),
        navigateToDeliveryLocation: createLogger(
          'Provide `navigateToLocation` action to handle delivery location navigation event',
        ),

        // ─── Optional Error Handlers ─────────────────

        onSearchByAreaError: createErrorLogger(
          'onSearchByAreaError',
          'Failed to fetch locations by search area',
        ),
        onCheckForLocationWarningError: createErrorLogger(
          'onCheckForLocationWarningError',
          'Failed to check location warning',
        ),
        onSearchByDeliveryPlaceIdError: createErrorLogger(
          'onSearchByDeliveryPlaceIdError',
          'Failed to fetch delivery location by placed ID',
        ),
        onSearchDeliveryAddressesError: createErrorLogger(
          'onSearchDeliveryAddressesError',
          'Failed to fetch delivery addresses',
        ),
        onAddDeliveryAddressIdError: createErrorLogger(
          'onAddDeliveryAddressIdError',
          'Failed to add new delivery address',
        ),

        // ─── Optional Event Listeners ────────────────

        onUserLocationChanged: createLogger(
          'Provide `onUserLocationChanged` action to handle user location changed event',
        ),
        onLocationTypeChangeEvent: createLogger(
          'Provide `onLocationTypeChangeEvent` action to handle location type change event',
        ),
        onSearchByAreaEvent: createLogger(
          'Provide `onSearchByAreaEvent` action to listen for "search by locations" event',
        ),
        onSearchByDeliveryPlaceIdEvent: createLogger(
          'Provide `onSearchByDeliveryPlaceIdEvent` action to listen for "search by delivery place ID" event',
        ),
        onLocationWarningConfirmEvent: createLogger(
          'Provide `onLocationWarningConfirmEvent` action to listen for "confirm location" event',
        ),
        onFocusedPinIdChangeEvent: createLogger(
          'Provide `onFocusedPinIdChangeEvent` action to listen for "focused pin ID change" event',
        ),
        onCurrentPositionChanged: createLogger(
          'Provide `onCurrentPositionChanged` action to handle user location changed event',
        ),
        onNavigateToLocationEvent: createLogger(
          'Provide `onNavigateToLocationEvent` action to listen for "navigate to location" event',
        ),
        onNavigateToDeliveryLocationEvent: createLogger(
          'Provide `onNavigateToDeliveryLocationEvent` action to listen for "navigate to delivery location" event',
        ),
      },
      guards: {
        // ─── Locations ───────────────────────────────

        checkIfEnteredMinChars: (_, { searchString }) =>
          searchString.length >= minimalCharactersForSearch,
        checkIfShouldFetchInitialLocationsByCurrentPosition: (context) =>
          shouldUseCurrentPosition && context.currentPosition !== undefined && !context.locations,
        checkIfShouldFetchLocationsByCurrentPosition: (context) =>
          shouldUseCurrentPosition && context.currentPosition !== undefined,
        checkIfShouldShowLocationWarning: (_, event) => event.data === true,
        checkIfAcceptableSearchQuery: (context) => {
          const searchQuery = context.predictionsSearchQuery ?? '';

          return searchQuery.trim().length >= minimalCharactersForSearch;
        },

        // ─── Recent And Nearby Locations ─────────────

        checkIfHaveRecentOrNearbyLocations: (context) => {
          const { locationSearchType, nearbyLocations } = context;

          const recentLocations = context.recentLocations ?? [];
          const nearbyPickupLocations =
            locationSearchType === 'pickup' ? (nearbyLocations?.pickup ?? []) : [];
          const nearbyOutpostLocations =
            locationSearchType === 'outpost' ? (nearbyLocations?.outpost ?? []) : [];

          return (
            recentLocations.length > 0 ||
            nearbyPickupLocations.length + nearbyOutpostLocations.length > 0
          );
        },

        // ─── Delivery Location ───────────────────────

        checkIfShouldGetDefaultDeliveryLocationOnEntry: ({ deliveryLocation }) =>
          !deliveryLocation || !deliveryLocation.restaurantSlug,
        checkIfShouldAddNewDeliveryLocation: ({ deliveryLocation }) => !deliveryLocation?.addressId,

        // ─── External Guards ─────────────────────────

        checkIfSignedIn: () => {
          createLogger('`checkIfSignedIn` guard is not provided');

          return false;
        },

        /**
         * A flag that indicates the need to show the delivery address form.
         */
        checkIfShouldShowDeliveryAddressForm: () => {
          createLogger('`checkIfShouldShowDeliveryAddressForm` guard is not provided');

          return false;
        },

        checkIfInitialOrderChannelEnabledAndSignedIn: () => {
          createLogger('`checkIfInitialOrderChannelEnabledAndSignedIn` guard is not provided');

          return false;
        },
        checkIfInitialOrderChannelEnabled: () => {
          createLogger('`checkIfInitialOrderChannelEnabled` guard is not provided');

          return false;
        },

        checkIfPickupOrderChannel: () => {
          createLogger('`checkIfPickupOrderChannel` guard is not provided');

          return false;
        },
        checkIfDeliveryOrderChannel: () => {
          createLogger('`checkIfDeliveryOrderChannel` guard is not provided');

          return false;
        },
        checkIfOutpostOrderChannel: () => {
          createLogger('`checkIfOutpostOrderChannel` guard is not provided');

          return false;
        },
      },
    },
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Handles rejected service promises by logging the corresponding error message
 * and action name, which can be supplied to externally handle the error.
 */
function createErrorLogger(missingActionName: string, errorMessage: string) {
  const message = `${errorMessage} (NOTE: Provide "${missingActionName}" action while setting up the machine to handle this error externally.)`;

  return createLogger(message, 'error');
}

/**
 * Returns a function to log messages in the location machine scope.
 */
function createLogger(message: string, messageType: 'error' | 'info' = 'info') {
  return function () {
    if (messageType === 'error') {
      messageLogger.error(message);

      return;
    }

    messageLogger.debug(message);
  };
}

// ─── Logger ──────────────────────────────────────────────────────────────────

LOG.enable('LOCATION STATE MACHINE');

const messageLogger = logger.extend('LOCATION STATE MACHINE');

// ─── Types ───────────────────────────────────────────────────────────────────

type CreateLocationSearchMachineParams = Readonly<{
  client: Client;
  googlePlacesApiKey: string;
  defaultLocationSearchType?: LocationSearchMachineContext['locationSearchType'];
  lastInteractedStoreAddressId?: string;
  minimalCharactersForSearch?: number;
  shouldUseCurrentPosition?: boolean;
  shouldUseRadarForPickup: boolean;
  shouldUseRadarForDelivery: boolean;

  /**
   * Tells machine to fetch extra upcharge disclosure fields from BE (delivery).
   */
  shouldUseDeliveryDisclosureFields: boolean;

  /**
   * Tells machine to fetch extra upcharge disclosure fields from BE (outpost).
   */
  shouldUseOutpostDisclosureFields: boolean;
}>;

export type LocationSearchService = InterpreterFrom<ReturnType<typeof createLocationSearchMachine>>;
